import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Tooltip } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fetchLowStockPathItems } from "../services/pathLowStockService";
import {
  canShowPathLowStockAlert,
  readPathLowStockQuantityThreshold,
} from "../utils/pathLowStock";
import { resolveOrganizationId } from "../utils/resolveOrganizationId";
import PathLowStockAlertDialog from "./PathLowStockAlertDialog";

const NavbarPathLowStockButton = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadLowStock = useCallback(async () => {
    if (!canShowPathLowStockAlert(user?.roleName)) return;

    const organizationId = resolveOrganizationId(user);
    if (!organizationId) return;

    setLoading(true);
    try {
      const list = await fetchLowStockPathItems(organizationId);
      setItems(list);
    } catch (error) {
      console.warn("[Navbar] Path low stock fetch failed:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadLowStock();
  }, [loadLowStock]);

  useEffect(() => {
    const onOrgUpdated = () => loadLowStock();
    window.addEventListener("userOrganizationIdUpdated", onOrgUpdated);
    return () => window.removeEventListener("userOrganizationIdUpdated", onOrgUpdated);
  }, [loadLowStock]);

  if (!user || !canShowPathLowStockAlert(user.roleName)) {
    return null;
  }

  const count = items.length;

  return (
    <>
      <Tooltip
        title={
          count > 0
            ? `${count} pathology item(s) low or out of stock — click to view`
            : "No low stock pathology items"
        }
      >
        <Badge badgeContent={count > 0 ? count : 0} color="error" max={99}>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<WarningAmberIcon />}
            onClick={() => setDialogOpen(true)}
            disabled={loading}
            sx={{ whiteSpace: "nowrap" }}
          >
            Pathology Low Stock
          </Button>
        </Badge>
      </Tooltip>

      <PathLowStockAlertDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        items={items}
        quantityThreshold={readPathLowStockQuantityThreshold(resolveOrganizationId(user))}
        onGoToInventory={() => {
          setDialogOpen(false);
          navigate("/inventory");
        }}
      />
    </>
  );
};

export default NavbarPathLowStockButton;
